/**
 * Emery 257 — Modular Referral & Journey Tracker
 *
 * Captures affiliate/referral parameters (?ref=...), persists a 30-day multi-touch
 * journey in localStorage, deduplicates session updates, and syncs attribution
 * directly to Shopify Cart Attributes (which automatically persist to Order
 * Note Attributes upon checkout completion).
 *
 * Falls back to ?utm_source when ?ref is absent/empty and ?utm_medium=affiliate
 * (case-insensitive), so affiliate links that only set UTM params are still
 * attributed. ?ref always takes precedence when present.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'emery_referral_data';
  var SESSION_SYNC_KEY = 'emery_ref_cart_synced';
  var TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var STOREFRONT_TOKEN = '2d29f9b07af1cfbebf54df02896c432c';

  // --- Cookie Helpers (Fallback & Session Support) ---
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return match ? decodeURIComponent(match[3]) : null;
  }

  function setSessionCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
  }

  // --- Storage Access with Safe Fallbacks ---
  function loadStoredData() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      // Check 30-day expiration
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveStoredData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // Storage full or restricted in private browsing mode
    }
  }

  // --- URL Extraction ---
  // Emery 257: falls back to utm_source when ref is absent/empty and
  // utm_medium=affiliate, so affiliate links carrying only UTM params (no
  // explicit ref) still get attributed. ref always wins when present.
  function extractRefParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      var ref = params.get('ref');
      if (ref && typeof ref === 'string') {
        ref = ref.trim();
        if (ref.length > 0 && ref.length <= 100) {
          return ref;
        }
      }

      var utmMedium = params.get('utm_medium');
      if (utmMedium && typeof utmMedium === 'string' && utmMedium.trim().toLowerCase() === 'affiliate') {
        var utmSource = params.get('utm_source');
        if (utmSource && typeof utmSource === 'string') {
          utmSource = utmSource.trim();
          if (utmSource.length > 0 && utmSource.length <= 100) {
            return utmSource;
          }
        }
      }
    } catch (e) {}
    return null;
  }

  // --- Capture & Attribution Logic ---
  function recordTouchpoint(ref) {
    var now = Date.now();
    var currentPath = window.location.pathname + window.location.search;
    var data = loadStoredData();

    if (!data) {
      // First visit with referral
      data = {
        firstTouch: {
          ref: ref,
          timestamp: now,
          landingUrl: currentPath
        },
        lastTouch: {
          ref: ref,
          timestamp: now,
          landingUrl: currentPath
        },
        history: [
          {
            ref: ref,
            timestamp: now,
            landingUrl: currentPath
          }
        ],
        expiresAt: now + TTL_MS
      };
    } else {
      // Returning visitor: Preserve firstTouch, update lastTouch
      var lastEntry = data.history && data.history.length > 0 ? data.history[data.history.length - 1] : null;
      var isDuplicateImmediate = lastEntry && lastEntry.ref.toLowerCase() === ref.toLowerCase() && (now - lastEntry.timestamp < 60000);

      data.lastTouch = {
        ref: ref,
        timestamp: now,
        landingUrl: currentPath
      };

      if (!isDuplicateImmediate) {
        if (!Array.isArray(data.history)) {
          data.history = [];
        }
        data.history.push({
          ref: ref,
          timestamp: now,
          landingUrl: currentPath
        });
      }

      // Rolling 30-day window renewal
      data.expiresAt = now + TTL_MS;

      // Invalidate session sync flag so the new lastTouch is pushed to the cart
      try {
        sessionStorage.removeItem(SESSION_SYNC_KEY);
      } catch (e) {}
      document.cookie = SESSION_SYNC_KEY + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    }

    saveStoredData(data);
    return data;
  }

  // GA4: expose the credited ref as a user property so later events are attributable to it.
  // Called on every page load from the stored last touch (not only when a ref is captured), so
  // returning visitors are covered. Queued on dataLayer instead of calling window.gtag, because
  // this deferred script can run before the Google & YouTube app defines gtag; gtag.js drains the
  // queue whenever it loads.
  function setAnalyticsRef() {
    var data = loadStoredData();
    var lastRef = data && data.lastTouch && data.lastTouch.ref;
    if (!lastRef) return;
    window.dataLayer = window.dataLayer || [];
    // Must push the `arguments` object, not an array; gtag.js ignores arrays.
    (function () {
      window.dataLayer.push(arguments);
    })('set', 'user_properties', { ref: lastRef });
  }

  // --- Summary Formatter for Order Attributes ---
  function buildJourneySummary(data) {
    if (!data || !Array.isArray(data.history) || data.history.length === 0) {
      return '';
    }
    return data.history.map(function (item) {
      var d = new Date(item.timestamp);
      var dateStr = d.toISOString().split('T')[0];
      return item.ref + ' (' + dateStr + ')';
    }).join(' -> ');
  }

  // --- Session Deduplication Check ---
  function isSessionAlreadySynced(lastRef) {
    if (!lastRef) return true;
    try {
      var sessionVal = sessionStorage.getItem(SESSION_SYNC_KEY);
      if (sessionVal && sessionVal.toLowerCase() === lastRef.toLowerCase()) {
        return true;
      }
    } catch (e) {}

    var cookieVal = getCookie(SESSION_SYNC_KEY);
    if (cookieVal && cookieVal.toLowerCase() === lastRef.toLowerCase()) {
      return true;
    }
    return false;
  }

  function markSessionSynced(lastRef) {
    try {
      sessionStorage.setItem(SESSION_SYNC_KEY, lastRef);
    } catch (e) {}
    setSessionCookie(SESSION_SYNC_KEY, lastRef);
  }

  // --- Live Cart Access (used to confirm writes actually landed) ---
  function fetchCart() {
    var checkCartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url)
      ? window.Theme.routes.cart_url + '.json'
      : '/cart.js';
    return fetch(checkCartUrl).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    });
  }

  function cartHasExpectedAttribution(cart, lastRef) {
    return !!(cart && cart.attributes && cart.attributes['Referral (Last Touch)'] === lastRef);
  }

  // Emery 257: Horizon's product-form.js dispatches CartLinesUpdateEvent
  // optimistically, before the /cart/add.js fetch resolves, and resolves (or
  // rejects) event.promise only once that request actually settles. Awaiting
  // it here prevents our /cart/update.js write from racing the add, which
  // could create a second cart or have its attributes clobbered by the add.
  function waitForCartAddToSettle(event) {
    var eventPromise = event && event.promise;
    if (eventPromise && typeof eventPromise.then === 'function') {
      return eventPromise.then(
        function () { return true; },
        function () {
          // The add itself failed; still attempt a sync against whatever
          // the cart actually ended up holding.
          return true;
        }
      );
    }

    // No deferred promise on this event (unknown/older dispatcher): give the
    // in-flight request a moment, then confirm against the live cart.
    return new Promise(function (resolve) {
      setTimeout(function () {
        fetchCart().then(function () { resolve(true); }).catch(function () { resolve(true); });
      }, 700);
    });
  }

  // --- Sync to Cart Attributes (/cart/update.js) ---
  var isSyncing = false;

  function syncCartAttributes(force) {
    var data = loadStoredData();
    if (!data || !data.lastTouch || !data.lastTouch.ref) {
      return;
    }

    var lastRef = data.lastTouch.ref;
    if (!force && isSessionAlreadySynced(lastRef)) {
      return;
    }

    if (isSyncing) return;
    isSyncing = true;

    var journeySummary = buildJourneySummary(data);
    var attributes = {
      'Referral (Last Touch)': data.lastTouch.ref,
      'Referral (First Touch)': data.firstTouch.ref,
      'Referral Journey': journeySummary,
      'Referral Data': JSON.stringify({
        firstTouch: data.firstTouch,
        lastTouch: data.lastTouch,
        history: data.history
      })
    };

    var updateUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url)
      ? window.Theme.routes.cart_update_url
      : '/cart/update.js';

    fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then(function (cart) {
        isSyncing = false;
        // Only mark this session synced once the cart /cart/update.js
        // actually returned confirms the write landed. If it doesn't
        // (e.g. this write lost a race), leave the flag unset so the
        // page-load and cart-view checks retry on the next opportunity.
        if (cartHasExpectedAttribution(cart, lastRef)) {
          markSessionSynced(lastRef);
        }
        // Optionally sync Storefront API Cart Metafields if cart has token
        if (STOREFRONT_TOKEN && cart && cart.token) {
          syncStorefrontCartMetafields(cart.token, data, journeySummary);
        }
      })
      .catch(function () {
        isSyncing = false;
      });
  }

  // --- Sync Storefront Cart Metafields (for cartToOrderCopyable) ---
  function syncStorefrontCartMetafields(cartToken, data, journeySummary) {
    if (!STOREFRONT_TOKEN || !cartToken) return;

    var cartId = 'gid://shopify/Cart/' + cartToken;
    var query = 'mutation cartMetafieldsSet($metafields: [CartMetafieldsSetInput!]!) {' +
      ' cartMetafieldsSet(metafields: $metafields) {' +
      '   userErrors { field message }' +
      ' }' +
      '}';

    var variables = {
      metafields: [
        {
          ownerId: cartId,
          key: 'referral.last_touch',
          type: 'single_line_text_field',
          value: data.lastTouch.ref
        },
        {
          ownerId: cartId,
          key: 'referral.first_touch',
          type: 'single_line_text_field',
          value: data.firstTouch.ref
        },
        {
          ownerId: cartId,
          key: 'referral.journey',
          type: 'single_line_text_field',
          value: journeySummary || data.lastTouch.ref
        }
      ]
    };

    fetch('/api/2026-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).catch(function () {
      // Non-blocking fallback
    });
  }

  // Confirms the live cart already carries the expected attribution before
  // trusting the session flag. If it doesn't match, forces a resync so a
  // write lost to the add-to-cart race self-heals here instead of being
  // permanently skipped for the rest of the session.
  function ensureCartAttributesSynced() {
    var data = loadStoredData();
    if (!data || !data.lastTouch || !data.lastTouch.ref) return;
    var lastRef = data.lastTouch.ref;

    fetchCart()
      .then(function (cart) {
        if (cartHasExpectedAttribution(cart, lastRef)) {
          markSessionSynced(lastRef);
          return;
        }
        syncCartAttributes(true);
      })
      .catch(function () {
        // Couldn't confirm the live cart state; fall back to the
        // session-flag gated sync rather than forcing an extra write.
        syncCartAttributes(false);
      });
  }

  // --- Cart Interaction Listeners ---
  function setupCartEventListeners() {
    // 1. Horizon standard event. Wait for the add to actually settle
    // before syncing (see waitForCartAddToSettle above).
    document.addEventListener('shopify:cart:lines-update', function (event) {
      waitForCartAddToSettle(event).then(function () {
        syncCartAttributes(false);
      });
    });

    // 2. Form submission to /cart/add
    document.addEventListener('submit', function (event) {
      var target = event.target;
      if (target && target.tagName === 'FORM') {
        var action = target.getAttribute('action') || '';
        if (action.indexOf('/cart/add') !== -1) {
          setTimeout(function () {
            syncCartAttributes(false);
          }, 600);
        }
      }
    }, true);

    // 3. Listen to drawer open or cart view. Verify against the live cart
    // rather than trusting the session flag alone.
    document.addEventListener('shopify:cart:view', function () {
      ensureCartAttributesSynced();
    });

    // 4. Initial check: if the user already has items in cart, confirm the
    // live cart's attributes match before trusting the session flag.
    fetchCart()
      .then(function (cart) {
        if (cart && cart.item_count > 0) {
          var data = loadStoredData();
          var lastRef = data && data.lastTouch && data.lastTouch.ref;
          if (lastRef && cartHasExpectedAttribution(cart, lastRef)) {
            markSessionSynced(lastRef);
          } else {
            syncCartAttributes(true);
          }
        }
      })
      .catch(function () {});
  }

  // --- Initialize Tracker ---
  function init() {
    // 1. Capture incoming ref param if present
    var incomingRef = extractRefParam();
    if (incomingRef) {
      recordTouchpoint(incomingRef);
    } else {
      // Ensure existing stored data is cleaned if expired
      loadStoredData();
    }

    setAnalyticsRef();

    // 2. Bind cart listeners
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupCartEventListeners);
    } else {
      setupCartEventListeners();
    }
  }

  // Public debug API
  window.EmeryReferral = {
    getData: loadStoredData,
    syncCart: function () { syncCartAttributes(true); },
    clearData: function () {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SESSION_SYNC_KEY);
    }
  };

  init();
})();
